import type { TopicMetadata } from '../types';

// Helper function to get topic ID (prefers e180, falls back to e179)
export const getTopicId = (topic: any): string | undefined => {
    return topic.e180_TopicId || topic.e179_TopicId;
};

// Helper function to get topic name (prefers e180, falls back to e179)
export const getTopicName = (topic: any): string | undefined => {
    return topic.e180_Name || topic.e179_Name;
};

// Helper function to get topic min hours (prefers e180, falls back to e179)
export const getTopicMinHours = (topic: any): number | undefined => {
    return topic.e180_MinHours || topic.e179_MinHours;
};

/** When total is null or undefined, nested children are treated as unknown (lazy-expand still allowed). */
function nullableCountMayHaveChildren(total: unknown): boolean {
    if (total == null) return true;
    return Number(total) > 0;
}

/** Provider curriculum (E179): use server TotalChildTopicCount to hide expand when there are no nested child topics. */
export function curriculumTopicMayHaveNestedChildTopics(topic: any): boolean {
    return nullableCountMayHaveChildren(topic?.e179_TotalChildTopicCount);
}

/** Student curriculum (E180): same for nested child topics under a parent topic. */
export function studentCurriculumTopicMayHaveNestedChildTopics(topic: any): boolean {
    return nullableCountMayHaveChildren(topic?.e180_TotalChildTopicCount);
}

/** E179 year row: TotalTopicCount indicates whether lazy-loading year topics can yield any. */
export function curriculumYearMayHaveTopics(yearData: any): boolean {
    return nullableCountMayHaveChildren(yearData?.e179_TotalTopicCount);
}

/** Library tree: year row topics have been fetched (or year has none to load). */
export function isLibraryYearTopicsLoaded(
    yearNodeId: string,
    yearData: any,
    loadedNodeIds: Set<string>,
    assumeTopicsLoaded = false
): boolean {
    if (assumeTopicsLoaded) return true;
    if (!curriculumYearMayHaveTopics(yearData)) return true;
    return loadedNodeIds.has(yearNodeId);
}

/** Show year-level bulk checkbox only after that year's topics are in the tree. */
export function canShowLibraryYearSelectionCheckbox(
    yearNodeId: string,
    yearData: any,
    loadedNodeIds: Set<string>,
    assumeTopicsLoaded = false
): boolean {
    if (!curriculumYearMayHaveTopics(yearData)) return false;
    return isLibraryYearTopicsLoaded(yearNodeId, yearData, loadedNodeIds, assumeTopicsLoaded);
}

type LibraryTreeYearChild = { type: string; id: string; data?: any };

/** Show key-stage bulk checkbox only after every loadable year under it has been fetched. */
export function canShowLibraryKeyStageSelectionCheckbox(
    yearChildren: LibraryTreeYearChild[],
    loadedNodeIds: Set<string>,
    assumeTopicsLoaded = false
): boolean {
    const loadableYears = yearChildren.filter(
        (y) => y.type === 'year' && curriculumYearMayHaveTopics(y.data)
    );
    if (loadableYears.length === 0) return false;
    return loadableYears.every((y) =>
        isLibraryYearTopicsLoaded(y.id, y.data, loadedNodeIds, assumeTopicsLoaded)
    );
}

/** E180 year row. */
export function studentCurriculumYearMayHaveTopics(yearData: any): boolean {
    return nullableCountMayHaveChildren(yearData?.e180_TotalTopicCount);
}

export const isLeafTopic = (topic: any): boolean => {
    const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics || [];
    return childTopics.length === 0;
};

export const getLeafTopics = (topics: any[]): any[] => {
    return topics.filter(isLeafTopic);
};

export const collectLeafTopics = (topic: any, metadata: TopicMetadata, providerKey: string | undefined): any[] => {
    const leafTopics: any[] = [];
    
    // Check if this topic has child topics (handle both e179 and e180)
    const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics || [];
    
    if (childTopics.length === 0) {
        // This is a leaf topic - add it
        const enhancedTopic = {
            ...topic,
            _metadata: metadata,
            e179_ProviderKey: topic.e179_ProviderKey || topic.e180_ProviderKey || providerKey
        };
        leafTopics.push(enhancedTopic);
    } else {
        // This is a parent topic - recursively collect leaf topics from children
        childTopics.forEach((childTopic: any) => {
            leafTopics.push(...collectLeafTopics(childTopic, metadata, providerKey));
        });
    }
    
    return leafTopics;
};

export const isTopicInStudentCurriculum = (topicId: string, studentCurriculumData: any): boolean => {
    if (!studentCurriculumData?.e180_Subjects || !topicId) return false;
    
    // Normalize topic ID for comparison (handle GUID format differences)
    const normalizeId = (id: any) => id?.toString().toLowerCase().trim();
    const normalizedTopicId = normalizeId(topicId);
    
    // Recursively check topics in a topic list
    const checkTopics = (topics: any[]): boolean => {
        if (!topics || topics.length === 0) return false;
        
        for (const topic of topics) {
            const providerTopicId = normalizeId(topic.e180_ProviderTopicId);
            if (providerTopicId === normalizedTopicId) {
                return true;
            }
            // Check child topics recursively
            if (topic.e180_ChildTopics && topic.e180_ChildTopics.length > 0) {
                if (checkTopics(topic.e180_ChildTopics)) {
                    return true;
                }
            }
        }
        return false;
    };
    
    // Check all subjects, key stages, and years
    for (const subject of studentCurriculumData.e180_Subjects) {
        for (const keyStage of subject.e180_KeyStages || []) {
            for (const year of keyStage.e180_Years || []) {
                // Check topics in this year (only if they've been loaded)
                if (year.e180_Topics && year.e180_Topics.length > 0) {
                    if (checkTopics(year.e180_Topics)) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
};

const normalizeStudentIdForCompare = (id: string | null | undefined): string =>
    id == null || id === '' ? '' : id.toString().toLowerCase().trim();

/** Like isTopicInStudentCurriculum, but ignores E180 payload if it is tagged for another student (stale event). */
export const isTopicInStudentCurriculumForSelectedStudent = (
    topicId: string,
    studentCurriculumData: any,
    selectedStudentId: string | null | undefined
): boolean => {
    if (!selectedStudentId) {
        return isTopicInStudentCurriculum(topicId, studentCurriculumData);
    }
    const e180Student = studentCurriculumData?.e180_StudentId ?? studentCurriculumData?.E180_StudentId;
    if (e180Student != null && String(e180Student).trim() !== '') {
        if (normalizeStudentIdForCompare(String(e180Student)) !== normalizeStudentIdForCompare(selectedStudentId)) {
            return false;
        }
    }
    return isTopicInStudentCurriculum(topicId, studentCurriculumData);
};

export const getTopicProviderKey = (topic: any): string | undefined =>
    topic?.e179_ProviderKey || topic?.e180_ProviderKey;

/** Shipped JSON catalogs use C286; tenant catalog (`custom`) is not in `BuiltInCurriculumCatalogQueries`. */
export function isTenantCustomCatalogProvider(providerKey: string | null | undefined): boolean {
    return providerKey === 'custom';
}

/**
 * True when every selected leaf is a tenant custom curriculum topic (`custom`) and none are on the
 * selected student's student curriculum (matches library checkbox disable logic).
 */
export const canDeleteSelectedTenantCustomCurriculumTopics = (
    selectedTopics: any[],
    studentCurriculumData: any,
    selectedStudentId: string | null | undefined
): boolean => {
    const leaves = getLeafTopics(selectedTopics);
    if (leaves.length === 0) return false;
    for (const t of leaves) {
        if (getTopicProviderKey(t) !== 'custom') return false;
        const id = getTopicId(t);
        if (!id) return false;
        if (isTopicInStudentCurriculumForSelectedStudent(id, studentCurriculumData, selectedStudentId)) {
            return false;
        }
    }
    return true;
};

const normalizeTopicIdString = (id: string) => id.toLowerCase().trim();

function collectLeafTopicIdsUnderStudentCurriculumNode(topic: any): Set<string> {
    const children = topic?.e180_ChildTopics || [];
    if (children.length === 0) {
        const id = getTopicId(topic);
        return id ? new Set([normalizeTopicIdString(String(id))]) : new Set();
    }
    const sink = new Set<string>();
    for (const c of children) {
        for (const x of collectLeafTopicIdsUnderStudentCurriculumNode(c)) {
            sink.add(x);
        }
    }
    return sink;
}

function studentCurriculumTopicNodeCoversAllSelectedLeaves(node: any, selectedIds: ReadonlySet<string>): boolean {
    const children = node?.e180_ChildTopics || [];
    if (children.length === 0) {
        return false;
    }
    const leavesUnder = collectLeafTopicIdsUnderStudentCurriculumNode(node);
    let covers = true;
    for (const id of selectedIds) {
        if (!leavesUnder.has(id)) {
            covers = false;
            break;
        }
    }
    if (covers) {
        return true;
    }
    for (const c of children) {
        if (studentCurriculumTopicNodeCoversAllSelectedLeaves(c, selectedIds)) {
            return true;
        }
    }
    return false;
}

/**
 * Same rule as backend create-unit: every selected leaf id must be a descendant leaf of one
 * internal topic node in the E180 student curriculum tree (user selected a whole branch).
 */
export function areAllSelectedLeafTopicIdsUnderSingleStudentCurriculumParent(
    studentCurriculumData: any | null | undefined,
    selectedLeafTopics: any[]
): boolean {
    const subjects = studentCurriculumData?.e180_Subjects;
    if (!subjects?.length) {
        return false;
    }
    const selectedIds = new Set(
        selectedLeafTopics
            .map((t) => getTopicId(t))
            .filter((x): x is string => x != null && String(x).trim() !== '')
            .map((x) => normalizeTopicIdString(String(x)))
    );
    if (selectedIds.size === 0) {
        return false;
    }
    for (const subject of subjects) {
        for (const ks of subject.e180_KeyStages || []) {
            for (const year of ks.e180_Years || []) {
                for (const root of year.e180_Topics || []) {
                    if (studentCurriculumTopicNodeCoversAllSelectedLeaves(root, selectedIds)) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
}

/**
 * Same rule as backend create-unit: every selected leaf is a year-root topic with no children.
 */
export function areAllSelectedLeafTopicIdsRootLeavesOnStudentCurriculum(
    studentCurriculumData: any | null | undefined,
    selectedLeafTopics: any[]
): boolean {
    const subjects = studentCurriculumData?.e180_Subjects;
    if (!subjects?.length) {
        return false;
    }
    const selectedIds = new Set(
        selectedLeafTopics
            .map((t) => getTopicId(t))
            .filter((x): x is string => x != null && String(x).trim() !== '')
            .map((x) => normalizeTopicIdString(String(x)))
    );
    if (selectedIds.size === 0) {
        return false;
    }
    const rootLeaves = new Set<string>();
    for (const subject of subjects) {
        for (const ks of subject.e180_KeyStages || []) {
            for (const year of ks.e180_Years || []) {
                for (const root of year.e180_Topics || []) {
                    const children = root?.e180_ChildTopics || [];
                    const id = getTopicId(root);
                    if (id && children.length === 0) {
                        rootLeaves.add(normalizeTopicIdString(String(id)));
                    }
                }
            }
        }
    }
    for (const id of selectedIds) {
        if (!rootLeaves.has(id)) {
            return false;
        }
    }
    return true;
}

/** Leaf topic shape used when seeding Create Unit from the curriculum tree. */
export type CreateUnitTopicChoice = {
    id: string;
    name: string;
    subjectId: string;
    subjectName: string;
    keyStageId: string;
    keyStageName: string;
    yearId: string;
    yearName: string;
    parentGroupId?: string;
};

export type CreateUnitTreeCompatibility = {
    canCreateUnit: boolean;
    unitCreationMode: 'SingleSubject' | 'Blended' | null;
    reason?: string;
};

function collectParentGroupIdByLeafId(
    topics: any[],
    parentGroupId: string | undefined,
    sink: Map<string, string | undefined>
): void {
    for (const topic of topics || []) {
        const children = topic?.e180_ChildTopics || [];
        const topicId = String(getTopicId(topic) ?? '').trim();
        const groupId = parentGroupId ?? (children.length > 0 && topicId ? topicId : undefined);
        if (children.length > 0) {
            collectParentGroupIdByLeafId(children, groupId, sink);
            continue;
        }
        if (topicId) {
            sink.set(normalizeTopicIdString(topicId), parentGroupId);
        }
    }
}

function buildParentGroupIdByLeafId(studentCurriculumData: any | null | undefined): Map<string, string | undefined> {
    const sink = new Map<string, string | undefined>();
    for (const subject of studentCurriculumData?.e180_Subjects || []) {
        for (const ks of subject.e180_KeyStages || []) {
            for (const year of ks.e180_Years || []) {
                collectParentGroupIdByLeafId(year.e180_Topics || [], undefined, sink);
            }
        }
    }
    return sink;
}

function uniqueLeafTopicsById(selectedTopics: any[]): any[] {
    const leaves = getLeafTopics(selectedTopics ?? []);
    const seen = new Set<string>();
    const unique: any[] = [];
    for (const leaf of leaves) {
        const id = getTopicId(leaf);
        if (!id) continue;
        const key = normalizeTopicIdString(String(id));
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(leaf);
    }
    return unique;
}

/**
 * Whether tree selection can open Create Unit, and which mode to seed when non-empty.
 * Empty selection → always allowed (wizard picks topics from scratch).
 */
export function getCreateUnitCompatibilityFromTreeSelection(
    studentCurriculumData: any | null | undefined,
    selectedTopics: any[],
    extractMetadata: (topic: any, curriculum: any) => TopicMetadata,
    maxTopics: number
): CreateUnitTreeCompatibility {
    const leaves = uniqueLeafTopicsById(selectedTopics);
    if (leaves.length === 0) {
        return { canCreateUnit: true, unitCreationMode: null };
    }

    const metas = leaves.map((leaf) => extractMetadata(leaf, studentCurriculumData));
    const subjectIds = new Set(
        metas.map((m) => m.subjectId?.trim()).filter((id): id is string => !!id)
    );

    if (leaves.length === 2 && subjectIds.size === 2) {
        return { canCreateUnit: true, unitCreationMode: 'Blended' };
    }

    if (subjectIds.size !== 1) {
        return {
            canCreateUnit: false,
            unitCreationMode: null,
            reason:
                'Select topics from one subject (single-subject), or exactly two topics from two subjects (blended).'
        };
    }

    if (leaves.length > maxTopics) {
        return {
            canCreateUnit: false,
            unitCreationMode: null,
            reason: `A unit can include at most ${maxTopics} topics.`
        };
    }

    const first = metas[0];
    const sameBranch = metas.every(
        (m) =>
            m.subjectId?.trim() === first.subjectId?.trim() &&
            m.keyStageId?.trim() === first.keyStageId?.trim() &&
            m.yearId?.trim() === first.yearId?.trim()
    );
    if (!sameBranch) {
        return {
            canCreateUnit: false,
            unitCreationMode: null,
            reason: 'All topics in a single-subject unit must be from the same subject, key stage, and year.'
        };
    }

    const underOneParent = areAllSelectedLeafTopicIdsUnderSingleStudentCurriculumParent(
        studentCurriculumData,
        leaves
    );
    const allRootLeaves = areAllSelectedLeafTopicIdsRootLeavesOnStudentCurriculum(
        studentCurriculumData,
        leaves
    );
    if (!underOneParent && !allRootLeaves) {
        return {
            canCreateUnit: false,
            unitCreationMode: null,
            reason:
                'Choose leaf topics from one parent topic group, or choose only root-level topics.'
        };
    }

    return { canCreateUnit: true, unitCreationMode: 'SingleSubject' };
}

/** Map a compatible tree selection to Create Unit wizard topic choices (order preserved). */
export function mapTreeSelectionToCreateUnitTopicChoices(
    studentCurriculumData: any | null | undefined,
    selectedTopics: any[],
    extractMetadata: (topic: any, curriculum: any) => TopicMetadata
): CreateUnitTopicChoice[] {
    const leaves = uniqueLeafTopicsById(selectedTopics);
    const parentByLeaf = buildParentGroupIdByLeafId(studentCurriculumData);
    const choices: CreateUnitTopicChoice[] = [];
    for (const leaf of leaves) {
        const id = getTopicId(leaf);
        if (!id) continue;
        const meta = extractMetadata(leaf, studentCurriculumData);
        const subjectId = meta.subjectId?.trim();
        const keyStageId = meta.keyStageId?.trim();
        const yearId = meta.yearId?.trim();
        if (!subjectId || !keyStageId || !yearId) continue;
        const parentGroupId = parentByLeaf.get(normalizeTopicIdString(String(id)));
        choices.push({
            id: String(id).trim(),
            name: getTopicName(leaf) || 'Untitled topic',
            subjectId,
            subjectName: meta.subjectName?.trim() || 'Subject',
            keyStageId,
            keyStageName: meta.keyStageName?.trim() || 'Key stage',
            yearId,
            yearName: meta.yearLabel?.trim() || 'Year',
            ...(parentGroupId ? { parentGroupId } : {})
        });
    }
    return choices;
}

export const convertTopic = (topic: any): any => {
    return {
        e179_Name: topic.e180_Name,
        e179_TopicId: topic.e180_TopicId,
        e179_Details: topic.e180_Details,
        e179_MinHours: topic.e180_MinHours,
        e179_PriorKnowledgeRequired: topic.e180_PriorKnowledgeRequired || [],
        e179_NationalCurriculumContent: topic.e180_NationalCurriculumContent || [],
        e179_Keywords: (topic.e180_Keywords || []).map((k: any) => ({
            e179_Keyword: k.e180_Keyword,
            e179_Description: k.e180_Description
        })),
        e179_LearningObjectives: topic.e180_LearningObjectives || [],
        e179_SupervisionLevel: topic.e180_SupervisionLevel,
        e179_Outcome: topic.e180_Outcome,
        e179_TeacherTip: topic.e180_TeacherTip,
        e179_Misconceptions: topic.e180_Misconceptions,
        e179_ProviderKey: topic.e180_ProviderKey,
        e179_State: topic.e180_State, // Preserve state information
        e179_ParentTopicNames: topic.e180_ParentTopicNames || [], // Preserve parent topic names (empty for custom topics)
        e179_ChildTopics: topic.e180_ChildTopics?.map((child: any) => convertTopic(child)) || []
    };
};

// Count all topics recursively (including nested children) - for year-level counts
const countAllTopicsRecursive = (topics: any[]): number => {
    if (!topics || topics.length === 0) return 0;
    
    let count = topics.length;
    for (const topic of topics) {
        const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics;
        if (childTopics && childTopics.length > 0) {
            count += countAllTopicsRecursive(childTopics);
        }
    }
    return count;
};

// Count only leaf topics recursively (topics with no children) - for year-level counts
const countLeafTopicsRecursive = (topics: any[]): number => {
    if (!topics || topics.length === 0) return 0;
    
    let count = 0;
    for (const topic of topics) {
        const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics;
        // Leaf topic = no child topics
        if (!childTopics || childTopics.length === 0) {
            count++;
        } else {
            // Recursively count leaf topics in children
            count += countLeafTopicsRecursive(childTopics);
        }
    }
    return count;
};

// Count child topics recursively for a single topic - for topic-level counts
const countChildTopicsRecursive = (topic: any): number => {
    if (!topic) return 0;
    const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics;
    if (!childTopics || childTopics.length === 0) return 0;
    
    let count = childTopics.length;
    for (const childTopic of childTopics) {
        count += countChildTopicsRecursive(childTopic);
    }
    return count;
};

// Update topic-level counts recursively
const updateTopicCountsRecursive = (topics: any[], isStudentCurriculum: boolean = false): void => {
    if (!topics) return;
    
    for (const topic of topics) {
        const count = countChildTopicsRecursive(topic);
        if (isStudentCurriculum || topic.e180_TopicId) {
            topic.e180_TotalChildTopicCount = count;
        } else {
            topic.e179_TotalChildTopicCount = count;
        }
        
        const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics;
        if (childTopics && childTopics.length > 0) {
            updateTopicCountsRecursive(childTopics, isStudentCurriculum);
        }
    }
};

// Update all counts for a curriculum structure (year-level and topic-level)
const updateAllCounts = (data: any, isStudentCurriculum: boolean = false): void => {
    if (!data) return;
    
    const subjects = isStudentCurriculum ? data.e180_Subjects : data.e179_Subjects;
    if (!subjects) return;
    
    for (const subject of subjects) {
        const keyStages = isStudentCurriculum ? subject.e180_KeyStages : subject.e179_KeyStages;
        if (!keyStages) continue;
        
        for (const keyStage of keyStages) {
            const years = isStudentCurriculum ? keyStage.e180_Years : keyStage.e179_Years;
            if (!years) continue;
            
            for (const year of years) {
                const topics = isStudentCurriculum ? year.e180_Topics : year.e179_Topics;
                if (topics) {
                    // Update year-level count (only count leaf topics)
                    const yearCount = countLeafTopicsRecursive(topics);
                    if (isStudentCurriculum) {
                        year.e180_TotalTopicCount = yearCount;
                    } else {
                        year.e179_TotalTopicCount = yearCount;
                    }
                    
                    // Update topic-level counts recursively
                    updateTopicCountsRecursive(topics, isStudentCurriculum);
                } else {
                    // No topics, set count to 0
                    if (isStudentCurriculum) {
                        year.e180_TotalTopicCount = 0;
                    } else {
                        year.e179_TotalTopicCount = 0;
                    }
                }
            }
        }
    }
};

// Prune empty containers (years, key stages, subjects) from curriculum data
const pruneEmptyContainers = (data: any, isStudentCurriculum: boolean = false): void => {
    if (!data) return;
    
    const subjects = isStudentCurriculum ? data.e180_Subjects : data.e179_Subjects;
    if (!subjects || subjects.length === 0) return;
    
    // Process in reverse order to avoid index shifting issues
    for (let subjectIndex = subjects.length - 1; subjectIndex >= 0; subjectIndex--) {
        const subject = subjects[subjectIndex];
        const keyStages = isStudentCurriculum ? subject.e180_KeyStages : subject.e179_KeyStages;
        
        if (!keyStages || keyStages.length === 0) {
            // Remove empty subject
            subjects.splice(subjectIndex, 1);
            continue;
        }
        
        // Process key stages in reverse order
        for (let keyStageIndex = keyStages.length - 1; keyStageIndex >= 0; keyStageIndex--) {
            const keyStage = keyStages[keyStageIndex];
            const years = isStudentCurriculum ? keyStage.e180_Years : keyStage.e179_Years;
            
            if (!years || years.length === 0) {
                // Remove empty key stage
                keyStages.splice(keyStageIndex, 1);
                continue;
            }
            
            // Process years in reverse order
            for (let yearIndex = years.length - 1; yearIndex >= 0; yearIndex--) {
                const year = years[yearIndex];
                const topics = isStudentCurriculum ? year.e180_Topics : year.e179_Topics;
                
                // Remove empty year (no topics or empty topics array)
                if (!topics || topics.length === 0) {
                    years.splice(yearIndex, 1);
                }
            }
            
            // After processing years, check if key stage is now empty
            const updatedYears = isStudentCurriculum ? keyStage.e180_Years : keyStage.e179_Years;
            if (!updatedYears || updatedYears.length === 0) {
                keyStages.splice(keyStageIndex, 1);
            }
        }
        
        // After processing key stages, check if subject is now empty
        const updatedKeyStages = isStudentCurriculum ? subject.e180_KeyStages : subject.e179_KeyStages;
        if (!updatedKeyStages || updatedKeyStages.length === 0) {
            subjects.splice(subjectIndex, 1);
        }
    }
};

export const removeTopicsFromData = (
    topicIds: string[],
    curriculumData: any,
    setCurriculumData: (updater: (prev: any) => any) => void,
    studentCurriculumData: any,
    setStudentCurriculumData: (updater: (prev: any) => any) => void
) => {
    // Normalize topic IDs for comparison
    const normalizeId = (id: any) => id?.toString().toLowerCase().trim();
    const normalizedTopicIds = new Set(topicIds.map(id => normalizeId(id)));
    
    // Helper to remove topics recursively from a topic list
    const removeTopicsFromList = (topics: any[], isStudentCurriculum: boolean = false): any[] => {
        if (!topics) return [];
        
        return topics
            .filter(topic => {
                const topicId = normalizeId(topic.e179_TopicId || topic.e180_TopicId);
                return !normalizedTopicIds.has(topicId);
            })
            .map(topic => {
                // Recursively remove from child topics
                const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics;
                if (childTopics && childTopics.length > 0) {
                    const updatedChildTopics = removeTopicsFromList(childTopics, isStudentCurriculum);
                    if (isStudentCurriculum || topic.e180_TopicId) {
                        return {
                            ...topic,
                            e180_ChildTopics: updatedChildTopics
                        };
                    } else {
                        return {
                            ...topic,
                            e179_ChildTopics: updatedChildTopics
                        };
                    }
                }
                return topic;
            });
    };
    
    // Remove from E179 curriculum data (provider view)
    if (curriculumData?.e179_Subjects) {
        setCurriculumData(prevData => {
            if (!prevData || !prevData.e179_Subjects) return prevData;
            
            const updatedData = JSON.parse(JSON.stringify(prevData)); // Deep clone
            for (const subject of updatedData.e179_Subjects) {
                for (const keyStage of subject.e179_KeyStages || []) {
                    for (const year of keyStage.e179_Years || []) {
                        if (year.e179_Topics) {
                            year.e179_Topics = removeTopicsFromList(year.e179_Topics, false);
                        }
                    }
                }
            }
            // Update counts after removing topics
            updateAllCounts(updatedData, false);
            // Prune empty years, key stages, and subjects
            pruneEmptyContainers(updatedData, false);
            return updatedData;
        });
    }
    
    // Remove from E180 student curriculum data
    if (studentCurriculumData?.e180_Subjects) {
        setStudentCurriculumData(prevData => {
            if (!prevData || !prevData.e180_Subjects) return prevData;
            
            const updatedData = JSON.parse(JSON.stringify(prevData)); // Deep clone
            for (const subject of updatedData.e180_Subjects) {
                for (const keyStage of subject.e180_KeyStages || []) {
                    for (const year of keyStage.e180_Years || []) {
                        if (year.e180_Topics) {
                            year.e180_Topics = removeTopicsFromList(year.e180_Topics, true);
                        }
                    }
                }
            }
            // Update counts after removing topics
            updateAllCounts(updatedData, true);
            // Prune empty years, key stages, and subjects
            pruneEmptyContainers(updatedData, true);
            return updatedData;
        });
    }
};

export const updateTopicDetails = (
    topicId: string,
    details: any,
    curriculumData: any,
    setCurriculumData: (updater: (prev: any) => any) => void,
    studentCurriculumData: any,
    setStudentCurriculumData: (updater: (prev: any) => any) => void
) => {
    // Normalize topic ID for comparison (handle GUID format)
    const normalizeId = (id: any) => id?.toString().toLowerCase().trim();
    const normalizedTopicId = normalizeId(topicId);
    
    const updateTopicInStructure = (topics: any[], isStudentCurriculum: boolean = false): boolean => {
        if (!topics) return false;

        for (const topic of topics) {
            const topicId1 = normalizeId(topic.e179_TopicId || topic.e180_TopicId);
            if (topicId1 === normalizedTopicId) {
                // Found the topic, update it
                // If it's a student curriculum topic, convert e179_* fields to e180_* fields
                if (isStudentCurriculum || topic.e180_TopicId) {
                    const e180Details: any = {};
                    if (details.e179_PriorKnowledgeRequired !== undefined) {
                        e180Details.e180_PriorKnowledgeRequired = details.e179_PriorKnowledgeRequired;
                    }
                    if (details.e179_NationalCurriculumContent !== undefined) {
                        e180Details.e180_NationalCurriculumContent = details.e179_NationalCurriculumContent;
                    }
                    if (details.e179_Keywords !== undefined) {
                        e180Details.e180_Keywords = details.e179_Keywords?.map((k: any) => ({
                            e180_Keyword: k.e179_Keyword || k.e180_Keyword,
                            e180_Description: k.e179_Description || k.e180_Description
                        }));
                    }
                    if (details.e179_LearningObjectives !== undefined) {
                        e180Details.e180_LearningObjectives = details.e179_LearningObjectives;
                    }
                    if (details.e179_SupervisionLevel !== undefined) {
                        e180Details.e180_SupervisionLevel = details.e179_SupervisionLevel;
                    }
                    if (details.e179_Outcome !== undefined) {
                        e180Details.e180_Outcome = details.e179_Outcome;
                    }
                    if (details.e179_TeacherTip !== undefined) {
                        e180Details.e180_TeacherTip = details.e179_TeacherTip;
                    }
                    if (details.e179_Misconceptions !== undefined) {
                        e180Details.e180_Misconceptions = details.e179_Misconceptions;
                    }
                    if (details.e179_DetailsLoaded !== undefined) {
                        e180Details.e180_DetailsLoaded = details.e179_DetailsLoaded;
                    }
                    Object.assign(topic, e180Details);
                } else {
                    // For provider topics, use e179_* fields as-is
                    Object.assign(topic, details);
                }
                return true;
            }

            // Recursively check child topics
            const childTopics = topic.e179_ChildTopics || topic.e180_ChildTopics;
            if (childTopics && updateTopicInStructure(childTopics, isStudentCurriculum)) {
                return true;
            }
        }

        return false;
    };
    
    // Update in provider curriculum data
    if (curriculumData?.e179_Subjects) {
        setCurriculumData(prevData => {
            if (!prevData || !prevData.e179_Subjects) return prevData;
            
            const updatedData = JSON.parse(JSON.stringify(prevData)); // Deep clone
            let found = false;
            for (const subject of updatedData.e179_Subjects) {
                for (const keyStage of subject.e179_KeyStages) {
                    for (const year of keyStage.e179_Years) {
                        if (updateTopicInStructure(year.e179_Topics)) {
                            found = true;
                            break;
                        }
                    }
                    if (found) break;
                }
                if (found) break;
            }
            return found ? updatedData : prevData;
        });
    }
    
    // Update in student curriculum data
    if (studentCurriculumData?.e180_Subjects) {
        setStudentCurriculumData(prevData => {
            if (!prevData || !prevData.e180_Subjects) return prevData;

            const updatedData = JSON.parse(JSON.stringify(prevData)); // Deep clone
            let found = false;
            for (const subject of updatedData.e180_Subjects) {
                for (const keyStage of subject.e180_KeyStages) {
                    for (const year of keyStage.e180_Years) {
                        if (updateTopicInStructure(year.e180_Topics, true)) {
                            found = true;
                            break;
                        }
                    }
                    if (found) break;
                }
                if (found) break;
            }
            return found ? updatedData : prevData;
        });
    }
};
